import { HTTP } from './http-common.js';
import notifier from './notifier.js';

/**
 * @typedef {number} ProtoId
 * @typedef {string} ProtoName
 */

/**
 * @typedef {function(): void} InitHandler
 */

class ProtoManager {
  constructor() {
    /** @type {?Map<ProtoId, ProtoName>} */
    this.aliasMap = null;

    /** @type {?Map<ProtoName, ProtoId>} */
    this.mapAliasToId = null;

    this.protoInfos = null;

    /** @type {InitHandler[]} */
    this.initHandlers = [];
  }

  init() {
    return HTTP.get('/config_alias_map')
    .then(response => {
      const aliasObj = response.data.data;
      this.aliasMap = createAliasMap(aliasObj);
      this.mapAliasToId = createMapAliasToId(this.aliasMap);
      this.protoInfos = createProtoInfos(this.aliasMap);

      for(const initHandler of this.initHandlers) {
        initHandler();
      }
      this.initHandlers = [];
    })
    .catch(err => {
      notifier.showError(err);
    });
  }

  isInited() {
    return this.aliasMap !== null;
  }

  /**
   * @param {InitHandler} handler 
   */
  addInitHandler(handler) {
    this.initHandlers.push(handler);
  }

  /**
   * @param {ProtoId} protoId 
   * @returns {string}
   */
  getAlias(protoId) {
    if(this.aliasMap === null) {
      return `protoId: ${protoId}. Error. Alias map is not inited.`;
    }

    const alias = this.aliasMap.get(protoId);
    if(alias === undefined) {
      return `Unknown proto ${protoId}`;
    }
    return alias;
  }

  /**
   * @param {ProtoId} protoId
   * @returns {string}
   */
  getAliasOrNone(protoId) {
    if(this.aliasMap === null) {
      return `protoId: ${protoId}. Error. Alias map is not inited.`;
    }

    const alias = this.aliasMap.get(protoId);
    if(alias === undefined) {
      return `None`;
    }
    return alias;
  }

  /**
   * @param {?string} alias 
   * @returns {?ProtoId}
   */
  getId(alias) {
    if(alias === null) {
      return null;
    }

    const mapAliasToId = this.mapAliasToId;
    if(mapAliasToId === null) {
      return null;
    }

    const protoId = mapAliasToId.get(alias);
    if(protoId === undefined) {
      return null;
    }
    return protoId;
  }

  getProtoInfos() {
    return this.protoInfos;
  }

  getSuggestions(str) {
    const MAX_AMOUNT = 7;

    const suggestions = [];
    for(const protoInfo of this.protoInfos) {
      if(!protoInfo.alias.includes(str)) {
        continue;
      }
      suggestions.push(protoInfo);
      if(suggestions.length === MAX_AMOUNT) {
        return suggestions;
      }
    }

    return suggestions;
  }
}


function createAliasMap(aliasObj) {
  const aliasMap = new Map();
  for(const protoIdStr in aliasObj) {
    const protoId = parseInt(protoIdStr, 10);
    const alias = aliasObj[protoId];
    aliasMap.set(protoId, alias);
  }
  return aliasMap;
}

function createProtoInfos(aliasMap) {
  const protoInfos = [];
  for(let [id, alias] of aliasMap) {
    protoInfos.push({id, alias});
  }
  protoInfos.sort(compareProtoInfosByAlias);
  return protoInfos;
}

function createMapAliasToId(mapIdToAlias) {
  const mapAliasToId = new Map();
  for(let [id, alias] of mapIdToAlias) {
    mapAliasToId.set(alias, id);
  }
  return mapAliasToId;
}

function compareProtoInfosByAlias(aProtoInfo, bProtoInfo) {
  if(aProtoInfo === bProtoInfo) {
    return 0;
  }

  const aAlias = aProtoInfo.alias;
  const bAlias = bProtoInfo.alias;
  if(aAlias < bAlias) {
    return -1;
  }
  if(aAlias > bAlias) {
    return 1;
  }
  return 0;
}

const protoManager = new ProtoManager();
export default protoManager;
